import React, { useCallback, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { COLORS } from '../config/colors';
import { FONTS } from '../config/fonts';
import {
  useStationsByDistance,
  type StationWithDistance,
} from '../hooks/useStationsByDistance';
import { strings } from '../strings';
import type { Station } from '../types/station';
import {
  getAQIColor,
  getContrastTextColor,
  getStationAQI,
  getStationListItemAccessibilityLabel,
} from '../utils/aqi';
import { getUserCoordinates, type Coordinates } from '../utils/geolocation';
import LocationIcon from './icons/LocationIcon';

type StationListPanelProps = {
  stations: Station[];
  mapCenter: Coordinates;
  onSelectStation: (station: Station) => void;
};

// Human readable distance for the list rows: metres below a kilometre, one
// decimal up to ten kilometres and whole kilometres beyond that.
const formatDistance = (distanceInKm: number): string => {
  if (distanceInKm < 1) {
    return `${Math.round(distanceInKm * 1000)} ${strings.metersShort}`;
  }

  if (distanceInKm < 10) {
    return `${distanceInKm.toFixed(1)} ${strings.kilometersShort}`;
  }

  return `${Math.round(distanceInKm)} ${strings.kilometersShort}`;
};

/**
 * Full-screen list of stations shown "instead of" the map.
 *
 * The stations are sorted against `mapCenter` (closest first) and the button
 * above the list reads the user's location to re-sort them again. The panel is
 * only mounted while it is toggled open (see `MapWrapper`), so the list itself
 * is loaded lazily; `FlatList` additionally virtualizes the rows.
 */
const StationListPanel = ({
  stations,
  mapCenter,
  onSelectStation,
}: StationListPanelProps) => {
  const insets = useSafeAreaInsets();
  // Reference point the list is sorted against, replaced when the user asks for
  // their current location.
  const [center, setCenter] = useState<Coordinates>(mapCenter);
  const [isLocating, setIsLocating] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const stationsByDistance = useStationsByDistance(stations, center);

  const handleGetLocation = useCallback(async () => {
    setIsLocating(true);
    setStatus(null);

    const coordinates = await getUserCoordinates();

    if (coordinates) {
      setCenter(coordinates);
      setStatus(strings.stationsSortedByLocation);
    } else {
      setStatus(strings.unableToDetermineLocation);
    }

    setIsLocating(false);
  }, []);

  const renderItem = useCallback(
    ({ item }: { item: StationWithDistance }) => {
      const aqi = getStationAQI(item);
      const aqiColor = getAQIColor(aqi);
      const distance = formatDistance(item.distanceInKm);

      return (
        <Pressable
          // Name, city, AQI, pollutants with their change, distance and the
          // action of the row (see `getStationListItemAccessibilityLabel`).
          accessibilityLabel={getStationListItemAccessibilityLabel(
            item,
            distance,
          )}
          accessibilityRole="button"
          onPress={() => onSelectStation(item)}
          style={({ pressed }) => [styles.row, pressed && styles.rowPressed]}
        >
          <View style={[styles.aqiBadge, { backgroundColor: aqiColor }]}>
            <Text
              style={[
                styles.aqiText,
                { color: getContrastTextColor(aqiColor) },
              ]}
            >
              {aqi ?? '—'}
            </Text>
          </View>
          <View style={styles.rowText}>
            <Text style={styles.rowName}>{item.name}</Text>
            <Text style={styles.rowCity}>{item.city}</Text>
          </View>
          <Text style={styles.rowDistance}>{distance}</Text>
        </Pressable>
      );
    },
    [onSelectStation],
  );

  const renderEmpty = () => (
    <View accessibilityLiveRegion="polite" style={styles.emptyState}>
      <Text style={styles.emptyText}>{strings.noStationsFound}</Text>
    </View>
  );

  return (
    <View style={styles.panel}>
      <View style={[styles.header, { paddingTop: insets.top + 68 }]}>
        <Text accessibilityRole="header" style={styles.title}>
          {strings.stationsListTitle}
        </Text>

        {/* Above the stations: read the user's location and re-sort the list. */}
        <Pressable
          accessibilityHint={strings.sortByLocationHint}
          accessibilityLabel={strings.sortByLocationLabel}
          accessibilityRole="button"
          accessibilityState={{ busy: isLocating }}
          onPress={handleGetLocation}
          style={({ pressed }) => [
            styles.locationButton,
            pressed && styles.locationButtonPressed,
          ]}
        >
          {isLocating ? (
            <ActivityIndicator
              color={COLORS.ink}
              size="small"
            />
          ) : (
            <LocationIcon color={COLORS.ink} />
          )}
          <Text style={styles.locationButtonText}>
            {isLocating ? strings.gettingLocation : strings.sortByLocationLabel}
          </Text>
        </Pressable>

        {/* Announced by screen readers when the list has been re-sorted (or
            when the location could not be read). */}
        {status ? (
          <Text accessibilityLiveRegion="polite" style={styles.status}>
            {status}
          </Text>
        ) : null}
      </View>

      <FlatList
        accessibilityLabel={strings.stationsListTitle}
        accessibilityRole="list"
        contentContainerStyle={[
          styles.listContent,
          { paddingBottom: insets.bottom + 24 },
        ]}
        data={stationsByDistance}
        keyExtractor={item => item.name}
        renderItem={renderItem}
        ListEmptyComponent={renderEmpty}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  panel: {
    direction: 'rtl',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: COLORS.surface,
  },
  header: {
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  title: {
    fontFamily: FONTS.regular,
    fontSize: 24,
    color: COLORS.ink,
    textAlign: 'center',
    marginBottom: 16,
  },
  // Same look as SearchBar, stretched into a full-width pill, capped at the
  // same width and centered so it never spans a very wide screen.
  locationButton: {
    direction: 'rtl',
    alignSelf: 'center',
    width: '100%',
    maxWidth: 500,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    backgroundColor: '#f3f4f6',
    borderRadius: 15,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  locationButtonPressed: {
    opacity: 0.8,
  },
  locationButtonText: {
    fontFamily: FONTS.regular,
    fontSize: 16,
    color: COLORS.ink,
  },
  status: {
    fontFamily: FONTS.regular,
    fontSize: 13,
    color: '#6b7280',
    textAlign: 'center',
    marginTop: 8,
  },
  listContent: {
    // Rows flow into as many columns as fit and wrap onto the next line.
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 12,
    paddingHorizontal: 16,
  },
  row: {
    direction: 'rtl',
    flexDirection: 'row',
    alignItems: 'center',
    width: 400,
    maxWidth: 500,
    gap: 12,
    paddingVertical: 14,
    paddingHorizontal: 14,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#e5e7eb',
  },
  rowPressed: {
    backgroundColor: '#f3f4f6',
  },
  aqiBadge: {
    minWidth: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  aqiText: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    fontWeight: '700',
  },
  rowText: {
    flex: 1,
  },
  rowName: {
    fontFamily: FONTS.regular,
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  rowCity: {
    fontFamily: FONTS.regular,
    fontSize: 13,
    color: '#6b7280',
    marginTop: 2,
  },
  rowDistance: {
    fontFamily: FONTS.regular,
    fontSize: 13,
    color: COLORS.ink,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  emptyText: {
    fontFamily: FONTS.regular,
    fontSize: 15,
    color: '#9ca3af',
  },
});

export default StationListPanel;
